/*  ------------------------------------------------------------------------ */

import { hmac as _hmac } from '@noble/hashes/hmac.js';
import { hex as base16,  base64 } from '@scure/base';
import { type CHash, utf8ToBytes } from '@noble/hashes/utils.js';
import type { ECDSA as CurveFn } from '@noble/curves/abstract/weierstrass.js';
import type { EdDSA as CurveFnEDDSA } from '@noble/curves/abstract/edwards.js';
import { secp256k1 } from '@noble/curves/secp256k1.js';
import { p256 as P256 } from '@noble/curves/nist.js';
import { numberToBytesLE, hexToBytes } from '@noble/curves/utils.js';
/*  ------------------------------------------------------------------------ */

type Input = string | Uint8Array;
type Hex = string | Uint8Array;

// @noble/hashes v2 and @noble/curves v2 accept Uint8Array only
// strings were treated as utf8 by noble-hashes v1 and as hex by noble-curves v1
const utf8Bytes = (data: Input): Uint8Array => ((typeof data === 'string') ? utf8ToBytes (data) : data);
const hexBytes = (data: Hex): Uint8Array => ((typeof data === 'string') ? hexToBytes (data) : data);

const encoders = {
    binary: x => x,
    hex: base16.encode,
    base64: base64.encode,
}

type Digest = 'binary' | 'hex' | 'base64'

const supportedCurve = {
    '1.3.132.0.10': secp256k1,
    '1.2.840.10045.3.1.7': P256,
}

/*  ----- minimal PEM / DER helpers (replace the vendored jsencrypt asn1js) ----- */

// strip the PEM armor (-----BEGIN/END ...-----) and base64-decode the body to DER bytes
function pemToDer (pem: string): Uint8Array {
    const body = pem
        .replace (/-----BEGIN [^-]+-----/g, '')
        .replace (/-----END [^-]+-----/g, '')
        .replace (/\s+/g, '');
    return base64.decode (body);
}

// read a definite-form DER length, returns [ length, nextOffset ]
function readDerLength (bytes: Uint8Array, offset: number): [number, number] {
    let length = bytes[offset];
    offset += 1;
    if (length & 0x80) {
        const numBytes = length & 0x7f;
        length = 0;
        for (let i = 0; i < numBytes; i++) {
            length = (length * 256) + bytes[offset];
            offset += 1;
        }
    }
    return [ length, offset ];
}

// parse a DER buffer into a flat list of its top-level TLV elements
function parseDerElements (bytes: Uint8Array): { tag: number, content: Uint8Array }[] {
    const elements = [];
    let offset = 0;
    while (offset < bytes.length) {
        const tag = bytes[offset];
        offset += 1;
        const result = readDerLength (bytes, offset);
        const length = result[0];
        offset = result[1];
        const content = bytes.slice (offset, offset + length);
        elements.push ({ 'tag': tag, 'content': content });
        offset += length;
    }
    return elements;
}

// decode an OID's content bytes into a dotted-decimal string
function decodeOid (bytes: Uint8Array): string {
    const values = [];
    const first = bytes[0];
    values.push (Math.floor (first / 40));
    values.push (first % 40);
    let value = 0;
    for (let i = 1; i < bytes.length; i++) {
        const b = bytes[i];
        value = (value * 128) + (b & 0x7f);
        if ((b & 0x80) === 0) {
            values.push (value);
            value = 0;
        }
    }
    return values.join ('.');
}

/*  .............................................   */

const hash = (request: Input, hash: CHash, digest: Digest = 'hex') => {
    const binary = hash (utf8Bytes (request))
    return encoders[digest] (binary)
};

/*  .............................................   */

const hmac = (request: Input, secret: Input, hash: CHash, digest: Digest = 'hex') => {
    const binary = _hmac (hash, utf8Bytes (secret), utf8Bytes (request))
    return encoders[digest] (binary)
};

/*  .............................................   */

function ecdsa (request: Hex, secret: Hex, curve: CurveFn, prehash: CHash | null = null, fixedLength = false) {
    if (prehash) {
        request = hash (request, prehash, 'hex')
    }
    if (typeof secret === 'string' && secret.length > 64) {
        // decode pem key
        if (secret.startsWith ('-----BEGIN EC PRIVATE KEY-----')) {
            const der = pemToDer (secret);
            const top = parseDerElements (der);
            // ECPrivateKey ::= SEQUENCE {
            //     version        INTEGER { ecPrivkeyVer1(1) } (ecPrivkeyVer1),
            //     privateKey     OCTET STRING,
            //     parameters [0] ECParameters {{ NamedCurve }} OPTIONAL,
            //     publicKey  [1] BIT STRING OPTIONAL
            // }
            const fields = parseDerElements (top[0].content);
            if (fields.length === 4) {
                const params = fields[2];
                if (params !== undefined && params.tag === 0xA0 && params.content.length > 0) {
                    const oid = decodeOid (parseDerElements (params.content)[0].content);
                    if (supportedCurve[oid] === undefined) throw new Error ('Unsupported curve');
                    curve = supportedCurve[oid];
                }
                secret = base16.encode (fields[1].content);
            } else {
                // maybe return false
                throw new Error('Unsupported key format');
            }
        } else {
            // maybe return false
            throw new Error('Unsupported key format');
        }
    }
    // @noble/curves v2 sign () accepts bytes only, returns encoded signature bytes
    // and expects an unhashed message by default (prehash: true), so prehash is disabled explicitly
    const messageBytes = hexBytes (request);
    const secretBytes = hexBytes (secret);
    let signature = curve.Signature.fromBytes (curve.sign (messageBytes, secretBytes, {
      lowS: true,
      prehash: false,
      format: 'recovered',
    }), 'recovered');
    const minimumSize = (BigInt(1) << (BigInt(8) * BigInt(31))) - BigInt(1);
    const halfOrder = ((curve.Point as any).Fn.ORDER as bigint) / BigInt(2);
    let counter = 0;
    while (fixedLength && (signature.r > halfOrder || signature.r <= minimumSize || signature.s <= minimumSize)) {
      signature = curve.Signature.fromBytes (curve.sign (messageBytes, secretBytes, {
        lowS: true,
        prehash: false,
        format: 'recovered',
        extraEntropy: numberToBytesLE(BigInt(counter), 32)
      }), 'recovered');
      counter += 1;
    }
    return {
        'r': signature.r.toString (16),
        's': signature.s.toString (16),
        'v': signature.recovery,
    }
}

function eddsa (request: Hex, secret: Input, curve: CurveFnEDDSA) {
    let privateKey: Uint8Array | undefined = undefined;
    if (secret.length === 32) {
      // ed25519 secret is 32 bytes
      privateKey = utf8Bytes (secret)
    } else if (typeof secret === 'string') {
      // secret is the base64 pem encoded key
      // we get the last 32 bytes
      privateKey = new Uint8Array (pemToDer (secret).slice (16))
    }
    const signature = curve.sign (hexBytes (request), privateKey as Uint8Array)
    return base64.encode (signature)
}

/*  ------------------------------------------------------------------------ */

// source: https://stackoverflow.com/a/18639975/1067003

function crc32 (str, signed = false) {
    const crcTable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
    if ((crc32 as any).table === undefined) {
        (crc32 as any).table = crcTable.split (' ').map (s => parseInt (s,16))
    }
    let crc = -1;
    for (let i = 0; i < str.length; i++) {
        // eslint-disable-next-line
        crc = (crc >>> 8) ^ (crc32 as any).table[(crc ^ str.charCodeAt (i)) & 0xFF];
    }
    // eslint-disable-next-line
    const unsigned = (crc ^ (-1)) >>> 0;
    if (signed && (unsigned >= 0x80000000)) {
        return unsigned - 0x100000000;
    } else {
        return unsigned;
    }
}

/*  ------------------------------------------------------------------------ */

export {
    hash,
    hmac,
    crc32,
    ecdsa,
    eddsa,
    pemToDer,
};

/*  ------------------------------------------------------------------------ */
